import { useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Box, Paper, TextField, Button, Typography, Alert, CircularProgress, Container,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { authApi } from "@/api/endpoints";
import { useAuth } from "@/store/auth";
import { errorMessage } from "@/api/client";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const setSession = useAuth((s) => s.setSession);

  const expired = params.get("reason") === "expired";
  const from = (location.state as { from?: string } | null)?.from ?? "/dashboard";

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      setSession(data);
      navigate(from, { replace: true });
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="xs">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h5" component="h1" gutterBottom textAlign="center" fontWeight={600}>
            Inventory Management
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
            Sign in to your account
          </Typography>

          {expired && (
            <Alert severity="info" sx={{ mb: 2 }}>Your session has expired. Please sign in again.</Alert>
          )}
          {loginMutation.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(loginMutation.error)}</Alert>
          )}

          <Box component="form" onSubmit={onSubmit}>
            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              autoFocus
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loginMutation.isPending}
              startIcon={loginMutation.isPending ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {loginMutation.isPending ? "Signing in..." : "Sign in"}
            </Button>
          </Box>
        </Paper>
        <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mt: 2 }}>
          v0.1.0
        </Typography>
      </Container>
    </Box>
  );
}
